import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Chat } from '../types';
import { Pin, MessageCircle } from 'lucide-react';
import './ChatList.css';

interface ChatListProps {
  chats: Chat[];
  selectedChatId?: string;
  onSelectChat: (chat: Chat) => void;
  searchQuery: string;
  pinnedChatIds: string[];
  onTogglePin?: (chatId: string) => void;
}

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChatId,
  onSelectChat,
  searchQuery,
  pinnedChatIds,
  onTogglePin: _onTogglePin,
}) => {
  const pinnedChats = chats.filter((chat) => pinnedChatIds.includes(chat.id));
  const unpinnedChats = chats.filter((chat) => !pinnedChatIds.includes(chat.id));

  const renderChatItem = (chat: Chat, isPinned: boolean) => {
    const isActive = selectedChatId === chat.id;
    const lastMessage = chat.messages[chat.messages.length - 1];
    const lastMessagePreview = lastMessage?.text.substring(0, 60) || 'No messages';
    const lastMessageDate = chat.lastMessageDate
      ? format(new Date(chat.lastMessageDate), 'MMM d, yyyy')
      : '';

    return (
      <motion.div
        key={chat.id}
        className={`chat-item ${isActive ? 'active' : ''}`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ backgroundColor: 'var(--hover-bg)' }}
        transition={{ duration: 0.2 }}
      >
        <div className="chat-avatar" onClick={() => onSelectChat(chat)}>
          <MessageCircle size={24} />
        </div>
        
        <div className="chat-info" onClick={() => onSelectChat(chat)}>
          <div className="chat-header-row">
            <h3 className="chat-title">{chat.title}</h3>
            {isPinned && (
              <Pin size={14} className="pin-icon" fill="currentColor" />
            )}
          </div>
          <p className="chat-preview">{lastMessagePreview}</p>
          <div className="chat-meta">
            <span className="message-count">{chat.messageCount} messages</span>
            {lastMessageDate && <span className="last-date">{lastMessageDate}</span>}
          </div>
        </div>
      </motion.div>
    );
  };

  if (chats.length === 0) {
    return (
      <div className="chat-list-empty">
        <MessageCircle size={48} className="empty-icon" />
        <h3>No chats found</h3>
        <p>
          {searchQuery
            ? 'Try a different search query'
            : 'Import your WhatsApp chat files to get started'}
        </p>
      </div>
    );
  }

  return (
    <div className="chat-list">
      {pinnedChats.length > 0 && (
        <div className="chat-section">
          <div className="section-header">
            <Pin size={14} />
            <span>Pinned</span>
          </div>
          {pinnedChats.map((chat) => renderChatItem(chat, true))}
        </div>
      )}
      
      {unpinnedChats.length > 0 && (
        <div className="chat-section">
          {pinnedChats.length > 0 && (
            <div className="section-header">
              <span>All Chats</span>
            </div>
          )}
          {unpinnedChats.map((chat) => renderChatItem(chat, false))}
        </div>
      )}
    </div>
  );
};
